import type {
  ConversationCreatedSocketEvent,
  ConversationListItem,
  Message,
  MessageDeletedSocketEvent,
  MessageListResponse,
  MessageSocketEvent,
  ReadReceiptSocketEvent,
  UnreadChangedSocketEvent,
  UnreadCountResponse
} from '@tandemone/types'
import type { QueryClient } from '@tanstack/react-query'
import type { Socket } from 'socket.io-client'

import {
  conversationDetailKey,
  conversationMessagesKey,
  CONVERSATIONS_LIST_KEY,
  CONVERSATIONS_UNREAD_KEY
} from './keys'

/** wires socket events to TanStack Query cache mutations. mirrors the
 * notification domain's socket-handlers pattern. installed once after the
 * user signs in (so the socket has a session cookie); returns a detach
 * function for cleanup on logout/unmount.
 *
 * the onIncomingMessage callback fires for *every* incoming message .
 * the bootstrap layer decides whether to fire a sonner toast based on
 * the user's current route (only toast when off-thread).
 */
export function installMessagingSocketHandlers(
  socket: Socket,
  queryClient: QueryClient,
  onIncomingMessage: (
    conversationId: string,
    message: Message,
    clientId: string | null
  ) => void
): () => void {
  function appendMessageToThread(
    conversationId: string,
    message: Message,
    clientId: string | null
  ): void {
    queryClient.setQueryData<
      { pages: MessageListResponse[]; pageParams: unknown[] } | undefined
    >(conversationMessagesKey(conversationId), old => {
      if (!old) return old
      /** dedup: if any page already contains this message id (the sender's
       * optimistic row will have the real id by now since onSuccess wrote
       * it back), skip the append. clientId match also dedups in case the
       * optimistic row hasn't been replaced yet.
       */
      const exists = old.pages.some(page =>
        page.items.some(
          existing =>
            existing.id === message.id ||
            (clientId !== null && existing.id === clientId)
        )
      )
      if (exists) return old
      /** messages are returned newest-first by the API; prepend to the
       * first page so the live thread shows fresh messages at the top of
       * the cache (the renderer flips order for display).
       */
      const [firstPage, ...rest] = old.pages
      if (!firstPage) return old
      return {
        ...old,
        pages: [{ ...firstPage, items: [message, ...firstPage.items] }, ...rest]
      }
    })
  }

  function patchConversationListWithMessage(
    conversationId: string,
    message: Message
  ): void {
    queryClient.setQueryData<{ items: ConversationListItem[] } | undefined>(
      CONVERSATIONS_LIST_KEY,
      old => {
        if (!old) return old
        const idx = old.items.findIndex(item => item.id === conversationId)
        if (idx === -1) {
          // not in cache . let the next refetch pick it up
          return old
        }
        const updated: ConversationListItem = {
          ...old.items[idx]!,
          updatedAt: message.createdAt,
          lastMessage: {
            body: message.body,
            senderUserName: message.senderUserName,
            createdAt: message.createdAt
          }
        }
        // move the freshly-touched conversation to the top of the list
        const next = old.items.filter((_, i) => i !== idx)
        next.unshift(updated)
        return { ...old, items: next }
      }
    )
  }

  function onMessage(event: MessageSocketEvent): void {
    appendMessageToThread(event.conversationId, event.message, event.clientId)
    patchConversationListWithMessage(event.conversationId, event.message)
    onIncomingMessage(event.conversationId, event.message, event.clientId)
  }

  function onConversationCreated(event: ConversationCreatedSocketEvent): void {
    queryClient.setQueryData<{ items: ConversationListItem[] } | undefined>(
      CONVERSATIONS_LIST_KEY,
      old => {
        if (!old) return { items: [event.conversation] }
        if (old.items.some(item => item.id === event.conversation.id)) {
          return old
        }
        return { ...old, items: [event.conversation, ...old.items] }
      }
    )
  }

  function onReadReceipt(event: ReadReceiptSocketEvent): void {
    queryClient.setQueryData<{ items: ConversationListItem[] } | undefined>(
      CONVERSATIONS_LIST_KEY,
      old => {
        if (!old) return old
        return {
          ...old,
          items: old.items.map(item => {
            if (item.id !== event.conversationId) return item
            return {
              ...item,
              participants: item.participants.map(participant =>
                participant.id === event.participantId
                  ? { ...participant, lastReadAt: event.lastReadAt }
                  : participant
              )
            }
          })
        }
      }
    )
    /** also patch the detail cache if it's open. detail uses a different
     * key so it needs its own write.
     */
    queryClient.setQueryData<
      | (ConversationListItem & {
          participants: ConversationListItem['participants']
        })
      | undefined
    >(conversationDetailKey(event.conversationId), old => {
      if (!old) return old
      return {
        ...old,
        participants: old.participants.map(participant =>
          participant.id === event.participantId
            ? { ...participant, lastReadAt: event.lastReadAt }
            : participant
        )
      }
    })
  }

  function onUnreadChanged(event: UnreadChangedSocketEvent): void {
    queryClient.setQueryData<UnreadCountResponse | undefined>(
      CONVERSATIONS_UNREAD_KEY,
      old => ({ count: Math.max(0, (old?.count ?? 0) + event.delta) })
    )
  }

  function onMessageDeleted(event: MessageDeletedSocketEvent): void {
    queryClient.setQueryData<
      { pages: MessageListResponse[]; pageParams: unknown[] } | undefined
    >(conversationMessagesKey(event.conversationId), old => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map(page => ({
          ...page,
          items: page.items.map(message =>
            message.id === event.messageId
              ? {
                  ...message,
                  body: '',
                  attachments: [],
                  deletedAt: event.deletedAt,
                  deletedByUserName: event.deletedByUserName
                }
              : message
          )
        }))
      }
    })
  }

  function onConnect(): void {
    /** on reconnect (or initial connect), invalidate caches so any events
     * missed during the offline window get backfilled.
     */
    void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_LIST_KEY })
    void queryClient.invalidateQueries({
      queryKey: CONVERSATIONS_UNREAD_KEY
    })
  }

  socket.on('messaging:message', onMessage)
  socket.on('messaging:conversation_created', onConversationCreated)
  socket.on('messaging:read_receipt', onReadReceipt)
  socket.on('messaging:unread_changed', onUnreadChanged)
  socket.on('messaging:message_deleted', onMessageDeleted)
  socket.on('connect', onConnect)

  return () => {
    socket.off('messaging:message', onMessage)
    socket.off('messaging:conversation_created', onConversationCreated)
    socket.off('messaging:read_receipt', onReadReceipt)
    socket.off('messaging:unread_changed', onUnreadChanged)
    socket.off('messaging:message_deleted', onMessageDeleted)
    socket.off('connect', onConnect)
  }
}
